import { Component, inject, input, output, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { KlGridFormColumn } from '../kl-grid-form';

@Component({
  selector: 'kl-grid-form-body-cell',
  standalone: false,
  templateUrl: './kl-grid-form-body-cell.html',
  host: { '[attr.data-binding]': 'col().binding', '[attr.data-row-index]': 'ri()' },
})
export class KlGridFormBodyCell<T extends object = Record<string, unknown>> {
  protected readonly translate = inject(TranslateService);

  readonly row = input.required<T>();
  readonly col = input.required<KlGridFormColumn<T>>();
  readonly ri = input.required<number>();
  readonly isReadOnly = input(false);
  readonly selectAllCheckbox = input(false);
  readonly primeCellEditFocusSelector = input('');
  readonly cellButtonPending = input<{ rowIndex: number; binding: string } | null>(null);

  readonly resolveSourceRowIndex = input.required<(filteredRowIndex: number) => number>();
  readonly getNested = input.required<(row: T, path: string) => unknown>();
  readonly formatDisplayValue = input.required<(row: T, col: KlGridFormColumn<T>, displayRowIndex: number) => string>();
  readonly resolveDateCellValue = input.required<(row: T, col: KlGridFormColumn<T>) => Date | null>();
  readonly datePickerFormat = input.required<string>();
  readonly pickerDisplayLabel = input.required<(row: T, col: KlGridFormColumn<T>) => string>();
  readonly pickerHasDisplayValue = input.required<(row: T, col: KlGridFormColumn<T>) => boolean>();

  readonly cellButtonClick = output<{
    row: T;
    col: KlGridFormColumn<T>;
    ri: number;
    event: Event;
  }>();
  readonly checkboxChange = output<{
    row: T;
    col: KlGridFormColumn<T>;
    value: boolean;
    rowIndex: number;
  }>();
  readonly selectChange = output<{
    row: T;
    col: KlGridFormColumn<T>;
    value: unknown;
    rowIndex: number;
  }>();
  readonly numberChange = output<{
    row: T;
    col: KlGridFormColumn<T>;
    value: unknown;
    rowIndex: number;
  }>();
  readonly dateChange = output<{
    row: T;
    col: KlGridFormColumn<T>;
    value: Date | null;
    rowIndex: number;
  }>();
  readonly textChange = output<{
    row: T;
    col: KlGridFormColumn<T>;
    value: string;
    rowIndex: number;
  }>();
  /**
   * Fires on Enter / blur for number and text cells — not on every keystroke. `numberChange`/
   * `textChange` still update the row live so the cell always displays what was typed; consumers
   * that recalculate (and replace row objects) on `cellChanged` should do it from this instead, so
   * PrimeNG's cell editor doesn't lose focus mid-typing.
   */
  readonly cellCommit = output<{
    row: T;
    col: KlGridFormColumn<T>;
    rowIndex: number;
  }>();
  readonly clearPickerClick = output<{ filteredRowIndex: number; col: KlGridFormColumn<T> }>();
  readonly pickerButtonClick = output<{ filteredRowIndex: number; col: KlGridFormColumn<T> }>();
  readonly pickerTextInput = output<{
    row: T;
    col: KlGridFormColumn<T>;
    value: string;
    rowIndex: number;
  }>();

  /** Typed picker text (`typablePicker`) since the last commit; `null` until the user edits the cell. */
  readonly typedPickerValue = signal<string | null>(null);

  /** Picker editor focus: the text input when typable, otherwise the lookup button (not the clear "x"). */
  readonly pickerFocusCellSelector = 'input,.kl-grid-form-picker-icon--lookup';

  /** True when the column uses {@link KlGridFormColumn.format} date editing (same as kl-grid). */
  isDateFormatColumn(): boolean {
    const format = this.col().format;
    return format === 'date' || format === 'date-time';
  }

  /**
   * Whether this cell uses PrimeNG `pEditableColumn` + `p-cellEditor` (click-to-edit with output/input templates).
   * False for read-only grids/columns/rows, structural columns, and the bulk `selected` checkbox when enabled.
   */
  usesPrimeCellEditing(): boolean {
    const col = this.col();
    if (
      this.isReadOnly() ||
      this.isColumnReadOnly() ||
      col.type === 'index' ||
      col.type === 'button' ||
      col.type === 'action-button'
    ) {
      return false;
    }
    if (this.selectAllCheckbox() && col.type === 'checkbox' && col.binding === 'selected') {
      return false;
    }
    return true;
  }

  /**
   * Whether to render an inline editor without Prime cell editor (always-on controls).
   * In read-only mode, only the row-selection `selected` checkbox stays editable.
   */
  isCellEditorMode(): boolean {
    const col = this.col();
    if (this.isColumnReadOnly()) {
      return false;
    }
    if (!this.isReadOnly()) {
      return true;
    }
    return col.type === 'checkbox' && col.binding === 'selected';
  }

  private isColumnReadOnly(): boolean {
    const col = this.col();
    return col.isReadOnly === true || col.isReadOnlyForRow?.(this.row()) === true;
  }

  onCellButtonClick(event: Event): void {
    this.cellButtonClick.emit({ row: this.row(), col: this.col(), ri: this.ri(), event });
  }

  /** Resolves `col().maxLength`, calling it with the current row when it's a per-row resolver. */
  resolveMaxLength(fallback: number): number {
    const ml = this.col().maxLength;
    if (typeof ml === 'function') {
      return ml(this.row());
    }
    return ml ?? fallback;
  }

  /** Resolves `col().options`, calling it with the current row when it's a per-row resolver. */
  resolveOptions(): unknown[] | undefined {
    const options = this.col().options;
    if (typeof options === 'function') {
      return options(this.row());
    }
    return options;
  }

  isActionDisabled(): boolean {
    const fn = this.col().button?.disabled;
    if (!fn) {
      return false;
    }
    return fn({ row: this.row(), column: this.col(), rowIndex: this.resolveSourceRowIndex()(this.ri()) });
  }

  onCheckboxChange(value: boolean): void {
    this.checkboxChange.emit({
      row: this.row(),
      col: this.col(),
      value,
      rowIndex: this.resolveSourceRowIndex()(this.ri()),
    });
  }

  onSelectChange(value: unknown): void {
    this.selectChange.emit({
      row: this.row(),
      col: this.col(),
      value,
      rowIndex: this.resolveSourceRowIndex()(this.ri()),
    });
  }

  onNumberChange(value: unknown): void {
    this.numberChange.emit({
      row: this.row(),
      col: this.col(),
      value,
      rowIndex: this.resolveSourceRowIndex()(this.ri()),
    });
  }

  onDateChange(value: Date | null): void {
    this.dateChange.emit({
      row: this.row(),
      col: this.col(),
      value,
      rowIndex: this.resolveSourceRowIndex()(this.ri()),
    });
  }

  onTextChange(value: string): void {
    this.textChange.emit({
      row: this.row(),
      col: this.col(),
      value,
      rowIndex: this.resolveSourceRowIndex()(this.ri()),
    });
  }

  commitCellEdit(): void {
    this.cellCommit.emit({
      row: this.row(),
      col: this.col(),
      rowIndex: this.resolveSourceRowIndex()(this.ri()),
    });
  }

  /**
   * Keeps a mouse text-selection drag targeted at the input even once the pointer strays outside
   * the cell (a narrow column + a long value). Without this, PrimeNG's `pEditableColumn` reads the
   * resulting mouseup/click as "clicked outside the cell" and closes the editor mid-selection.
   */
  onCellInputPointerDown(event: PointerEvent): void {
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  /** Selects the whole value on focus so typing replaces it instead of inserting at the cursor. */
  selectAllOnFocus(event: Event): void {
    (event.target as HTMLInputElement)?.select();
  }

  pickerInputValue(): string {
    const typed = this.typedPickerValue();
    return typed ?? this.pickerDisplayLabel()(this.row(), this.col());
  }

  onPickerTextTyped(value: string): void {
    this.typedPickerValue.set(value);
  }

  /** Fires on Enter / blur only — not on every keystroke — so the lookup runs once per commit. */
  commitPickerText(): void {
    const value = this.typedPickerValue();
    if (value == null) {
      return;
    }
    this.typedPickerValue.set(null);
    this.pickerTextInput.emit({
      row: this.row(),
      col: this.col(),
      value,
      rowIndex: this.resolveSourceRowIndex()(this.ri()),
    });
  }

  onClearPickerClick(): void {
    this.clearPickerClick.emit({ filteredRowIndex: this.ri(), col: this.col() });
  }

  onPickerButtonClick(): void {
    this.pickerButtonClick.emit({ filteredRowIndex: this.ri(), col: this.col() });
  }
}
